import * as React from 'react'
import { Outlet, useOutletContext } from 'react-router-dom'

import { EmployeesCustomTablesListPageDocument } from 'generated/resources/graphql'

import Loading from 'modules/core/components/Loading'
import { PortalWrapper } from 'modules/core/components/PortalWrapper'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import { useStrictParams } from 'modules/core/hooks/useStrictParams'
import i18n from 'modules/core/lib/i18n'

import resources from 'modules/custom_resources/store/collections/customResources/resources'
import values from 'modules/custom_resources/store/collections/customResources/values'
import Schema from 'modules/custom_resources/store/models/customResources/Schema'
import { fetchData } from 'modules/employees/components/CustomResources/SchemaComponent/utils'
import List from 'modules/employees/components/CustomTables/List'
import type { CommonRouteContext } from 'modules/employees/lib/types'
import Page from 'modules/layout/components/Page'
import Header from 'modules/layout/components/Page/Header'

import { type RootRoute, RouteContext } from './context'

interface Props {
  rootRoute: RootRoute
}

export default function CustomTablesListPage({ rootRoute }: Props) {
  const { headerTitle } = useOutletContext<CommonRouteContext>()
  const { id: employeeIdparam } = useStrictParams<'id'>()
  const { employee: sessionEmployee } = useCurrentSession()

  const employeeId = employeeIdparam ? Number(employeeIdparam) : sessionEmployee.id

  const [loading, setLoading] = React.useState<boolean>(true)

  const { data } = useQuery(EmployeesCustomTablesListPageDocument, {
    variables: {
      employeeId: employeeId,
    },
  })

  const canCustomizeTables = data?.employees._policies.manageCustomTableSettings.can ?? false
  const currency = data?.employees?.employee?.legalEntity?.currency ?? ''
  const canAddCustomTableRecords = data?.employees._policies.addCustomTableRecords.can ?? false
  const schemas = data?.customResources.schemas ?? []

  React.useEffect(() => {
    const fetchEmployeeCustomTables = async () => {
      setLoading(true)

      await fetchData({
        topicNames: [Schema.TOPIC_NAMES.employees, Schema.TOPIC_NAMES.performance],
        attachableType: 'Employee',
        attachableId: employeeId,
      })

      setLoading(false)
    }

    fetchEmployeeCustomTables()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeId])

  return (
    <RouteContext.Provider value={rootRoute}>
      <Page.Body>
        <PortalWrapper container={headerTitle}>
          <Header.Title>{i18n.t('custom_tables.title')}</Header.Title>
        </PortalWrapper>

        {loading && <Loading />}
        {!loading && (
          <>
            <List
              attachableId={employeeId}
              attachableType="Employee"
              resources={resources.models as any}
              schemas={schemas}
              currency={currency}
              values={values.models}
              canManage={canCustomizeTables}
              canAddCustomTableRecords={canAddCustomTableRecords}
            />
            <Outlet />
          </>
        )}
      </Page.Body>
    </RouteContext.Provider>
  )
}
